/**
 * The pen every film is drawn with: ink on drafting paper, by hand.
 *
 * Three habits make a line look drawn rather than plotted, and all three are here:
 *
 * - **Wobble.** No stroke is straight. A segment bows a little off its chord and its
 *   ends land a little off their marks.
 * - **Boil.** The wobble is redrawn a few times a second (`BOIL_RATE`), never every
 *   frame, the way a hand-drawn film's lines shimmer from one drawing to the next. Each
 *   primitive takes the next number in the frame's sequence as its seed, so a line
 *   keeps its wobble between boils even while it moves.
 * - **Overshoot.** A straight edge runs a few units past its corner, the way a drafter's
 *   lines cross at the corners of a plan. It is the house style of a company that sells
 *   drawings.
 *
 * Fills are flat washes laid a little off the line work, as a two-color print is when the
 * plates do not quite register. Everything is deterministic: the same frame at the same
 * time is the same picture, which is what lets a film be recorded frame by frame.
 *
 * Widths are in stage units and scale with the camera; `weight` is the camera's answer
 * to that, so a close-up does not draw with a marker.
 */

import type { Palette } from "./palette.js";

/** How many times a second the line work is redrawn. Traditional animation boils at 8–12. */
export const BOIL_RATE = 8;

export interface Pt {
  x: number;
  y: number;
}

export interface Stroke {
  color?: string;
  width?: number;
  alpha?: number;
  /** Run straight edges past their corners. On by default for lines and shapes. */
  overshoot?: boolean;
  /** How much the line wanders, as a multiple of the pen's own. */
  wobble?: number;
  /** How much of the stroke is drawn, 0–1: a line drawing itself on. */
  draw?: number;
}

export interface Paint extends Stroke {
  fill?: string;
  fillAlpha?: number;
  /** How far the wash sits off the line work. */
  offset?: number;
  /** False for a wash with no outline. */
  stroke?: boolean;
}

export interface TextStyle {
  size?: number;
  weight?: number;
  color?: string;
  align?: CanvasTextAlign;
  baseline?: CanvasTextBaseline;
  alpha?: number;
  rotate?: number;
  /** Letter spacing in units of the font size, for the title block's capitals. */
  tracking?: number;
}

const DEFAULT_WIDTH = 3;
const WOBBLE = 1.4;

/** A deterministic hash of two integers into [-1, 1]. */
export function noise(a: number, b: number): number {
  let h = Math.imul((a | 0) ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul((b | 0) + 0x7f4a7c15, 0xc2b2ae35);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  return ((h >>> 0) / 4294967295) * 2 - 1;
}

export class Ink {
  private seq = 0;

  /**
   * @param tick which boil this frame belongs to: `Math.floor(t * BOIL_RATE)`.
   * @param weight multiplies every width — the inverse of the camera's zoom.
   */
  constructor(
    readonly ctx: CanvasRenderingContext2D,
    readonly palette: Palette,
    private readonly tick: number,
    public weight = 1,
  ) {}

  /** The next primitive's seed, and a few numbers off it. */
  private next(): (k: number) => number {
    const seq = this.seq++;
    const tick = this.tick;
    return (k) => noise(seq * 31 + k, tick);
  }

  private begin(style: Stroke): void {
    const { ctx } = this;
    ctx.strokeStyle = style.color ?? this.palette.ink;
    ctx.lineWidth = (style.width ?? DEFAULT_WIDTH) * this.weight;
    ctx.globalAlpha = style.alpha ?? 1;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }

  /** One wobbling segment into the current path. */
  private segment(
    a: Pt,
    b: Pt,
    n: (k: number) => number,
    k: number,
    style: Stroke,
    overshoot: boolean,
    move: boolean,
  ): void {
    const { ctx } = this;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    if (length < 0.01) {
      return;
    }
    const ux = dx / length;
    const uy = dy / length;
    const amp = WOBBLE * (style.wobble ?? 1) * this.weight;
    const over = overshoot ? Math.min(5, length * 0.05) * (0.6 + 0.4 * n(k)) * this.weight : 0;
    const x0 = a.x - ux * over + n(k + 1) * amp * 0.6;
    const y0 = a.y - uy * over + n(k + 2) * amp * 0.6;
    let x1 = b.x + ux * over + n(k + 3) * amp * 0.6;
    let y1 = b.y + uy * over + n(k + 4) * amp * 0.6;
    const bow = n(k + 5) * amp * Math.min(1.4, length / 90);
    let mx = (x0 + x1) / 2 - uy * bow;
    let my = (y0 + y1) / 2 + ux * bow;
    const drawn = style.draw ?? 1;
    if (drawn < 1) {
      // A quadratic split at `drawn` (de Casteljau): the part of the curve already inked.
      const t = Math.max(0, drawn);
      const ax = x0 + (mx - x0) * t;
      const ay = y0 + (my - y0) * t;
      const bx = mx + (x1 - mx) * t;
      const by = my + (y1 - my) * t;
      x1 = ax + (bx - ax) * t;
      y1 = ay + (by - ay) * t;
      mx = ax;
      my = ay;
    }
    if (move) {
      ctx.moveTo(x0, y0);
    }
    ctx.quadraticCurveTo(mx, my, x1, y1);
  }

  line(x1: number, y1: number, x2: number, y2: number, style: Stroke = {}): void {
    const n = this.next();
    this.begin(style);
    this.ctx.beginPath();
    this.segment({ x: x1, y: y1 }, { x: x2, y: y2 }, n, 0, style, style.overshoot ?? true, true);
    this.ctx.stroke();
    this.ctx.globalAlpha = 1;
  }

  /** A polyline (or polygon) whose edges are each drawn as their own stroke. */
  poly(points: readonly Pt[], closed: boolean, style: Stroke = {}): void {
    const n = this.next();
    this.begin(style);
    const { ctx } = this;
    const count = closed ? points.length : points.length - 1;
    const overshoot = style.overshoot ?? closed;
    const drawn = style.draw ?? 1;
    ctx.beginPath();
    for (let i = 0; i < count; i += 1) {
      const portion = Math.min(1, Math.max(0, drawn * count - i));
      if (portion <= 0) {
        break;
      }
      const a = points[i]!;
      const b = points[(i + 1) % points.length]!;
      this.segment(a, b, n, i * 8, { ...style, draw: portion }, overshoot, true);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  /** A smooth open curve through the points: tails, cables, a swoosh under a word. */
  curve(points: readonly Pt[], style: Stroke = {}): void {
    if (points.length < 2) {
      return;
    }
    const n = this.next();
    this.begin(style);
    const { ctx } = this;
    const amp = WOBBLE * (style.wobble ?? 1) * this.weight * 0.6;
    const jitter = (p: Pt, i: number): Pt => ({ x: p.x + n(i * 2) * amp, y: p.y + n(i * 2 + 1) * amp });
    const pts = points.map(jitter);
    const drawn = style.draw ?? 1;
    const last = Math.max(1, Math.ceil((pts.length - 1) * drawn));
    ctx.beginPath();
    ctx.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i <= last && i < pts.length; i += 1) {
      const p = pts[i]!;
      const prev = pts[i - 1]!;
      if (i === pts.length - 1 || i === last) {
        const t = Math.min(1, (pts.length - 1) * drawn - (i - 1));
        ctx.quadraticCurveTo(prev.x, prev.y, prev.x + (p.x - prev.x) * t, prev.y + (p.y - prev.y) * t);
      } else {
        ctx.quadraticCurveTo(prev.x, prev.y, (prev.x + p.x) / 2, (prev.y + p.y) / 2);
      }
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  /** A filled polygon: the wash, off-register, then the outline over it. */
  shape(points: readonly Pt[], paint: Paint = {}): void {
    if (paint.fill !== undefined) {
      this.wash(points, paint.fill, paint.fillAlpha ?? 1, paint.offset ?? 2);
    }
    if (paint.stroke !== false) {
      this.poly(points, true, paint);
    }
  }

  /** A flat wash with no line, its corners nudged so it never sits exactly on the ink. */
  wash(points: readonly Pt[], color: string, alpha = 1, offset = 2): void {
    const n = this.next();
    const { ctx } = this;
    const jitter = 0.8 * this.weight;
    const ox = offset * this.weight;
    const oy = offset * 0.7 * this.weight;
    ctx.fillStyle = color;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    points.forEach((p, i) => {
      const x = p.x + ox + n(i * 2) * jitter;
      const y = p.y + oy + n(i * 2 + 1) * jitter;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  rect(x: number, y: number, w: number, h: number, paint: Paint = {}): void {
    this.shape(
      [
        { x, y },
        { x: x + w, y },
        { x: x + w, y: y + h },
        { x, y: y + h },
      ],
      paint,
    );
  }

  ellipse(cx: number, cy: number, rx: number, ry: number, paint: Paint = {}, rotation = 0): void {
    const n = this.next();
    const steps = Math.max(10, Math.min(40, Math.round((rx + ry) * 0.5)));
    const amp = WOBBLE * (paint.wobble ?? 1) * this.weight * 0.5;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const drawn = paint.draw ?? 1;
    const at = (i: number, shift: number): Pt => {
      const a = (i / steps) * Math.PI * 2 + shift;
      const wob = n(i % steps) * amp;
      const x = Math.cos(a) * (rx + wob);
      const y = Math.sin(a) * (ry + wob);
      return { x: cx + x * cos - y * sin, y: cy + x * sin + y * cos };
    };
    const shift = n(97) * 0.5;
    const { ctx } = this;
    if (paint.fill !== undefined) {
      const ox = (paint.offset ?? 1.5) * this.weight;
      ctx.fillStyle = paint.fill;
      ctx.globalAlpha = paint.fillAlpha ?? 1;
      ctx.beginPath();
      for (let i = 0; i < steps; i += 1) {
        const p = at(i, shift);
        if (i === 0) {
          ctx.moveTo(p.x + ox, p.y + ox * 0.7);
        } else {
          ctx.lineTo(p.x + ox, p.y + ox * 0.7);
        }
      }
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    if (paint.stroke === false) {
      return;
    }
    this.begin(paint);
    ctx.beginPath();
    // A hand closing a circle overlaps where it started.
    const total = Math.ceil((steps + 1.5) * drawn);
    let first = at(0, shift);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i <= total; i += 1) {
      const p = at(i, shift);
      const mid = { x: (first.x + p.x) / 2, y: (first.y + p.y) / 2 };
      ctx.quadraticCurveTo(first.x, first.y, mid.x, mid.y);
      first = p;
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  circle(cx: number, cy: number, r: number, paint: Paint = {}): void {
    this.ellipse(cx, cy, r, r, paint);
  }

  /** An arc of a circle, from `a0` to `a1` radians: a smile, a wheel's spokes, a swing. */
  arc(cx: number, cy: number, r: number, a0: number, a1: number, style: Stroke = {}): void {
    const steps = Math.max(3, Math.round((Math.abs(a1 - a0) * r) / 8));
    const points: Pt[] = [];
    for (let i = 0; i <= steps; i += 1) {
      const a = a0 + ((a1 - a0) * i) / steps;
      points.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
    this.curve(points, style);
  }

  dot(x: number, y: number, r: number, color = this.palette.ink, alpha = 1): void {
    const { ctx } = this;
    ctx.fillStyle = color;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(x, y, r * this.weight, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  text(content: string, x: number, y: number, style: TextStyle = {}): void {
    const { ctx } = this;
    const size = style.size ?? 28;
    ctx.save();
    ctx.translate(x, y);
    if (style.rotate !== undefined) {
      ctx.rotate(style.rotate);
    }
    ctx.font = `${style.weight ?? 700} ${size}px ${this.palette.font}`;
    ctx.fillStyle = style.color ?? this.palette.ink;
    ctx.globalAlpha = style.alpha ?? 1;
    ctx.textAlign = style.align ?? "center";
    ctx.textBaseline = style.baseline ?? "middle";
    if (style.tracking !== undefined && "letterSpacing" in ctx) {
      (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing =
        `${style.tracking * size}px`;
    }
    ctx.fillText(content, 0, 0);
    ctx.restore();
  }

  /** How wide a string sets in a style, for laying out a line of type. */
  measure(content: string, style: TextStyle = {}): number {
    const { ctx } = this;
    ctx.save();
    ctx.font = `${style.weight ?? 700} ${style.size ?? 28}px ${this.palette.font}`;
    if (style.tracking !== undefined && "letterSpacing" in ctx) {
      (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing =
        `${style.tracking * (style.size ?? 28)}px`;
    }
    const width = ctx.measureText(content).width;
    ctx.restore();
    return width;
  }
}
